import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../api/supabase';

export default function PackageList() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from('packages')
      .select(`*,
        package_suppliers(
          package_supplier_rates(cost_per_pax, budgeted_fx_rate, is_included, currency:currency_id(code))
        ),
        package_selling_prices(sell_price_per_pax, currency:currency_id(code), pax_from, pax_to)
      `)
      .order('created_at', { ascending: false });
    if (q) query = query.or(`package_name.ilike.%${q}%,destination.ilike.%${q}%,package_code.ilike.%${q}%`);
    const { data } = await query;
    setPackages(data || []);
    setLoading(false);
  }, [q]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Packages</div><div className="page-subtitle">Tour packages with supplier rates and costing</div></div>
        <Link to="/packages/new" className="btn btn-primary">+ New Package</Link>
      </div>
      <div className="card">
        <div style={{ marginBottom: 14 }}>
          <input className="form-input" placeholder="Search packages..." value={q} onChange={e => setQ(e.target.value)} style={{ maxWidth: 320 }} />
        </div>
        {loading ? <div className="spinner" /> : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Code</th><th>Package Name</th><th>Country</th><th>Type</th><th>Days</th>
                  <th>Departure</th><th style={{ textAlign: 'right' }}>Pax</th><th style={{ textAlign: 'right' }}>Cost/Pax (LKR)</th>
                  <th style={{ textAlign: 'right' }}>Sell/Pax</th><th style={{ textAlign: 'right' }}>Est. Revenue</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {packages.map(p => {
                  // Sum non-included rates across all suppliers
                  const costLKR = (p.package_suppliers || []).reduce((sum, ps) => {
                    const r = ps.package_supplier_rates?.[0];
                    if (!r || r.is_included) return sum;
                    return sum + parseFloat(r.cost_per_pax || 0) * parseFloat(r.budgeted_fx_rate || 1);
                  }, 0);
                  // Lowest selling price tier
                  const prices = p.package_selling_prices || [];
                  const lowestPrice = prices.length ? prices.reduce((min, sp) => parseFloat(sp.sell_price_per_pax) < parseFloat(min.sell_price_per_pax) ? sp : min, prices[0]) : null;
                  const gpPct = lowestPrice && costLKR > 0 ? ((parseFloat(lowestPrice.sell_price_per_pax) - costLKR) / parseFloat(lowestPrice.sell_price_per_pax) * 100).toFixed(0) : null;
                  // Estimated revenue: the pax count this package was built for (min_pax) ×
                  // whichever selling-price tier actually covers that pax count.
                  const builtForPax = p.min_pax || null;
                  const matchedPrice = builtForPax
                    ? (prices.find(sp => builtForPax >= sp.pax_from && builtForPax <= sp.pax_to) || lowestPrice)
                    : lowestPrice;
                  const estRevenue = builtForPax && matchedPrice ? builtForPax * parseFloat(matchedPrice.sell_price_per_pax) : null;

                  return (
                    <tr key={p.id}>
                      <td style={{ fontFamily: 'monospace', color: 'var(--text-muted)', fontSize: 12 }}>{p.package_code}</td>
                      <td style={{ fontWeight: 500 }}>{p.package_name}</td>
                      <td>{p.country || ''}{p.destination ? ', ' + p.destination : ''}</td>
                      <td>{p.package_type ? <span className="badge badge-blue">{p.package_type}</span> : ''}</td>
                      <td style={{ textAlign: 'center' }}>{p.num_days || ''}</td>
                      <td>{p.departure_date?.slice(0, 10) || ''}</td>
                      <td style={{ textAlign: 'right' }} className="amount">{builtForPax || <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}</td>
                      <td style={{ textAlign: 'right' }} className="amount">
                        {costLKR > 0 ? <span style={{ fontWeight: 600 }}>{costLKR.toLocaleString()}</span> : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>No rates</span>}
                      </td>
                      <td style={{ textAlign: 'right' }} className="amount">
                        {lowestPrice
                          ? <span>
                              <span style={{ fontWeight: 600 }}>{lowestPrice.currency?.code} {parseFloat(lowestPrice.sell_price_per_pax).toLocaleString()}</span>
                              {gpPct && <span style={{ fontSize: 11, color: 'var(--success)', marginLeft: 6 }}>GP {gpPct}%</span>}
                            </span>
                          : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
                      </td>
                      <td style={{ textAlign: 'right' }} className="amount">
                        {estRevenue
                          ? <span style={{ fontWeight: 600 }}>{matchedPrice.currency?.code} {estRevenue.toLocaleString()}</span>
                          : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
                      </td>
                      <td><span className={`badge ${p.is_active ? 'badge-green' : 'badge-gray'}`}>{p.is_active ? 'Active' : 'Inactive'}</span></td>
                      <td><Link to={`/packages/${p.id}`} className="btn btn-secondary btn-sm">View</Link></td>
                    </tr>
                  );
                })}
                {packages.length === 0 && <tr><td colSpan={12}><div className="empty-state"><div className="empty-state-title">No packages yet</div></div></td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
